// Analytics formatting + range helpers (MIS-813 / Task 32).
//
// Pure, dependency-free helpers shared by the analytics page components. The
// real worker analytics endpoints (apps/worker/src/routes/analytics.ts) take an
// ET-day window as optional `from`/`to` query params (YYYY-MM-DD, America/
// New_York). There is NO server-side `range=today|7d|...` param — the SPA owns
// the presets and converts each to a concrete ET-day `{ from, to }` pair here so
// the worker stays a thin window query. `formatRange` produces the human label
// for the active preset.

/** Range presets surfaced by the page's picker. `custom` defers to user dates. */
export type RangePreset = 'today' | 'yesterday' | '7d' | '30d' | 'custom';

/** A concrete ET-day window passed to the analytics endpoints. */
export interface DateRange {
  /** Inclusive start, YYYY-MM-DD in America/New_York. */
  from: string;
  /** Inclusive end, YYYY-MM-DD in America/New_York. */
  to: string;
}

/** Compact number formatter for the funnel cards (1.2K, 3.4M). */
const compactNumber = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1,
});

/** Plain integer formatter with thousands separators (1,234). */
const plainNumber = new Intl.NumberFormat('en-US');

/** Format a count for a metric card — compact above 10k, exact below. */
export function formatCount(n: number): string {
  return n >= 10_000 ? compactNumber.format(n) : plainNumber.format(n);
}

/** Format integer cents as "$X.XX" (e.g. 50000 → "$500.00"). */
export function formatCurrency(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);
}

/** Format a 0..1 ratio as a whole-number percent ("48%"). Guards divide-by-0. */
export function formatPercent(numerator: number, denominator: number): string {
  if (denominator <= 0) return '—';
  return `${Math.round((numerator / denominator) * 100)}%`;
}

/**
 * ROAS = revenue / cost, rendered as "2.5x". Returns "-" when cost is 0 (no
 * spend → ROAS undefined), per the issue's attribution rule.
 */
export function calculateRoas(revenueCents: number, costCents: number): string {
  if (costCents <= 0) return '-';
  return `${(revenueCents / costCents).toFixed(1)}x`;
}

const PRESET_LABELS: Record<Exclude<RangePreset, 'custom'>, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
};

/**
 * Human-readable label for the active range. For `custom` it shows the concrete
 * `from`–`to` window (or just "Custom" when dates aren't set yet).
 */
export function formatRange(preset: RangePreset, range?: DateRange): string {
  if (preset !== 'custom') return PRESET_LABELS[preset];
  if (range?.from && range?.to) return `${range.from} – ${range.to}`;
  return 'Custom';
}

/** The YYYY-MM-DD label for a Date in America/New_York (ET calendar day). */
export function toEtDay(d: Date): string {
  // en-CA yields ISO-ish YYYY-MM-DD; the timeZone forces the ET calendar day so
  // the label lines up with the worker's etDayWindow bucketing.
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);
}

/**
 * Resolve a preset to a concrete ET-day `{ from, to }` window. `today` and
 * `yesterday` are single days; `7d`/`30d` span back from today inclusive. For
 * `custom`, callers pass their own range, so this returns undefined.
 */
export function presetToRange(preset: RangePreset): DateRange | undefined {
  if (preset === 'custom') return undefined;
  const now = new Date();
  const today = toEtDay(now);
  if (preset === 'today') return { from: today, to: today };

  const dayMs = 24 * 60 * 60 * 1000;
  if (preset === 'yesterday') {
    const y = toEtDay(new Date(now.getTime() - dayMs));
    return { from: y, to: y };
  }
  const span = preset === '7d' ? 6 : 29; // inclusive of today
  const from = toEtDay(new Date(now.getTime() - span * dayMs));
  return { from, to: today };
}
